import { IElementType, IElementTypeHint } from "@faro-lotv/ielement-types";

/** Fields of an element that are needed to place it in the project tree */

/** Sections whose 360 photos are taken on their own or as part of a time series */
const SINGLE_CAPTURE_TYPE_HINTS = new Set([IElementTypeHint.room, IElementTypeHint.timeTravel]);

/** Whether the element is a section with the given type hint */
function isSectionWithTypeHint(element, typeHint) {
  return element?.type === IElementType.section && element.typeHint === typeHint;
}

/** @returns the closest ancestor of the element that matches the predicate */
function findAncestor(element, elementsById, predicate) {
  let ancestor = element.parentId ? elementsById.get(element.parentId) : undefined;
  while (ancestor && !predicate(ancestor)) {
    ancestor = ancestor.parentId ? elementsById.get(ancestor.parentId) : undefined;
  }
  return ancestor;
}

/** @returns the ID of the area the element is in, if any */
function findAreaId(element, elementsById) {
  return findAncestor(element, elementsById, ancestor => isSectionWithTypeHint(ancestor, IElementTypeHint.area))?.id;
}

/** @returns the 360 photos taken on their own or in a time series, i.e. in a room or time travel section */
function getSingleCaptures(structure, elementsById) {
  return structure.filter(element => element.type === IElementType.img360 && !!element.parentId && elementsById.get(element.parentId)?.type === IElementType.section && SINGLE_CAPTURE_TYPE_HINTS.has(elementsById.get(element.parentId)?.typeHint)).map(element => ({
    id: element.id,
    kind: "single",
    createdAt: element.createdAt,
    areaId: findAreaId(element, elementsById)
  }));
}

/** @returns the trajectory 360 photos that belong to a VideoMode dataset, leaving out those of scanners */
function getVideoModeCaptures(trajectoryImages, elementsById) {
  return trajectoryImages.flatMap(element => {
    const session = findAncestor(element, elementsById, ancestor => isSectionWithTypeHint(ancestor, IElementTypeHint.dataSetVideoWalk));
    if (!session) {
      return [];
    }
    return {
      id: element.id,
      kind: "videoMode",
      createdAt: element.createdAt,
      areaId: findAreaId(session, elementsById),
      videoModeSessionId: session.id
    };
  });
}

/**
 * Collects the 360 photos of the project and the areas they are in.
 *
 * @param structure Lightweight structure of the project. It leaves out the trajectory photos.
 * @param trajectoryImages The Img360 elements with the OdometryPath type hint, of VideoMode and scanners alike
 */
export function getCapturedData(structure, trajectoryImages) {
  const elementsById = new Map(structure.map(element => [element.id, element]));
  const areas = structure.filter(element => isSectionWithTypeHint(element, IElementTypeHint.area)).map(({
    id,
    name
  }) => ({
    id,
    name: name?.trim() ?? ""
  }));
  return {
    captures: [...getSingleCaptures(structure, elementsById), ...getVideoModeCaptures(trajectoryImages, elementsById)],
    areas
  };
}